import { useState } from 'react'
import { CheckCircle2, RotateCcw, XCircle } from 'lucide-react'
import { Alert, Confirm, Modal, useToast } from '@/components/ui'
import { enqueue } from '@/lib/sync'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { can, chainLength, chainStep, isStandIn, mayClearStep } from '@/lib/domain'
import type { OutboxKind } from '@/lib/db'

type Action = 'APPROVE' | 'REVIEW' | 'REJECT' | 'RETURN'

const CAPABILITY: Record<string, string> = {
  PR: 'approvePr',
  GRN: 'approveGrn',
  MIN: 'approveStoreDocs',
  MRN: 'approveStoreDocs',
}

const KIND: Record<string, OutboxKind> = {
  PR: 'pr.action', PO: 'po.action', GRN: 'grn.action', MIN: 'min.action', MRN: 'mrn.action',
}

/**
 * The three-button approval control that every document uses.
 *
 *   Approve  — moves the document on and (for GRN/MIN/MRN) posts inventory.
 *   Reject   — TERMINAL. The document is dead; the originator must start again.
 *   Return   — sends it back to the originator, still editable.
 */
type PostingBlock = {
  blocked: boolean
  reason: string | null
  posting_month: string
  suggested_month: string
}

export function DocumentActions({
  docType, docId, status, chainVersion, currentStep, sitePmId, creatorId, onDone, className,
}: {
  docType: 'PR' | 'PO' | 'GRN' | 'MIN' | 'MRN'
  docId: string
  status: string
  /** 2 = the Board's chain. Absent or 1 = the workflow in force before it. */
  chainVersion?: number | null
  /** Which stage of the chain the document is sitting at. */
  currentStep?: number | null
  /** Project Manager assigned to the document's site. PR stage 2 only. */
  sitePmId?: string | null
  /** Who raised the document. PO stage 1 only. */
  creatorId?: string | null
  onDone?: () => void
  className?: string
}) {
  const { profile } = useAuth()
  const toast = useToast()
  const [action, setAction] = useState<Action | null>(null)
  const [blocked, setBlocked] = useState<PostingBlock | null>(null)
  const [moving, setMoving] = useState(false)

  const role = profile?.role
  const isPo = docType === 'PO'

  // Is this document living under the Board's chain, or the workflow that was
  // in force when it was raised? Documents already in flight on 11-08-2026
  // keep the old rules to the end of their lives.
  const chained = (docType === 'PR' || docType === 'PO') && (chainVersion ?? 1) >= 2
  const stage = chained && status === 'SUBMITTED' && currentStep
    ? chainStep(docType as 'PR' | 'PO', currentStep)
    : null

  // A PO under the OLD workflow takes two signatures: Head of Stores reviews,
  // Head of Procurement approves.
  const poStage: 'review' | 'approve' | null = chained ? null
    : isPo && status === 'SUBMITTED' ? 'review'
      : isPo && status === 'REVIEWED' ? 'approve' : null

  const allowed = chained
    ? !!stage && mayClearStep(docType as 'PR' | 'PO', stage.step, role, {
        myId: profile?.id, sitePmId, creatorId,
      })
    : isPo
      ? (poStage === 'review' ? can(role, 'reviewPo') : poStage === 'approve' ? can(role, 'approvePo') : false)
      : status === 'SUBMITTED' && can(role, CAPABILITY[docType])

  if (!allowed) return null

  const total = chained ? chainLength(docType as 'PR' | 'PO') : 0
  const isLast = !!stage && stage.step === total
  const standIn = !!stage && isStandIn(docType as 'PR' | 'PO', stage.step, role)

  const primaryAction: Action = poStage === 'review' ? 'REVIEW' : 'APPROVE'
  const primaryLabel = poStage === 'review' ? 'Approve review'
    : stage ? `Approve — stage ${stage.step} of ${total}`
      : 'Approve'

  // WHY THIS ASKS THE SERVER BEFORE QUEUEING
  //
  // Approvals go through the offline outbox, so a refusal from the database
  // surfaces in the Sync Queue minutes later — right for a dropped connection,
  // wrong for a decision somebody has to make while looking at the document.
  //
  // A month can close between the day a receipt is entered and the day it is
  // approved. The posting date was resolved at entry and nothing re-checks it
  // on the way through, so this asks first and offers the two ways forward.
  async function submit(a: Action, reason?: string) {
    if (a === 'APPROVE' && ['GRN', 'MIN', 'MRN'].includes(docType)) {
      const { data } = await supabase.rpc('rpc_posting_block_for_doc', {
        p_doc_type: docType, p_doc_id: docId,
      })
      const b = data as PostingBlock | null
      if (b?.blocked) { setBlocked(b); setAction(null); return }
    }
    try {
      await enqueue({
        kind: KIND[docType],
        docType,
        label: `${a} ${docType} ${docId}`,
        localDocId: docId,
        payload: { doc_type: docType, doc_id: docId, action: a, comment: reason || null },
      })
      const verb = a === 'REVIEW' ? 'reviewed' : a === 'APPROVE' ? 'approved'
        : a === 'REJECT' ? 'rejected' : 'returned for revision'
      toast.success(`${docType} ${docId} ${verb}.`)
      setAction(null)
      onDone?.()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not record the action')
    }
  }

  const copy: Record<Action, { title: string; message: string; label: string; tone: 'success' | 'danger' | 'warning'; reason?: boolean; reasonLabel?: string }> = {
    APPROVE: {
      title: stage
        ? `Approve ${docId} — stage ${stage.step} of ${total}, ${stage.label}?`
        : `Approve ${docType} ${docId}?`,
      message: stage
        ? [
            stage.hint,
            standIn
              ? 'You are approving in place of the second Procurement Executive. That will be recorded against this order as a stand-in approval, and it will be visible to the CMD.'
              : null,
            isLast
              ? chainApproveFinal(docType)
              : `It then goes to stage ${stage.step + 1} — ${chainStep(docType as 'PR' | 'PO', stage.step + 1)?.label}.`,
          ].filter(Boolean).join(' ')
        : approveMessage(docType),
      label: 'Yes, approve', tone: 'success',
    },
    REVIEW: {
      title: `Approve your review of ${docId}?`,
      message: 'The purchase order moves to the Procurement Manager for final approval. You are confirming the vendor, rates, quantities, taxes and terms are correct.',
      label: 'Yes, review is clear', tone: 'success',
    },
    REJECT: {
      title: `Reject ${docType} ${docId}?`,
      message: 'Rejection is permanent. This document is stopped in the system for good and the originator will have to raise a completely fresh one. Use "Return for revision" instead if it only needs a correction.',
      label: 'Yes, reject permanently', tone: 'danger', reason: true, reasonLabel: 'Reason for rejection',
    },
    RETURN: {
      title: `Return ${docType} ${docId} for revision?`,
      message: chained
        ? 'The document goes back to the person who created it, still editable and with everything they entered intact. When they resubmit it, the approval chain starts again from stage 1 — a revised document is a different commitment, and everyone who cleared the old figures has to see the new ones.'
        : 'The document goes back to the person who created it. They can correct it and resubmit — nothing is lost.',
      label: 'Yes, send it back', tone: 'warning', reason: true, reasonLabel: 'What needs to change?',
    },
  }

  const c = action ? copy[action] : null

  return (
    <>
      <div className={className ?? 'flex flex-wrap gap-2'}>
        <button className="btn-success" onClick={() => setAction(primaryAction)}>
          <CheckCircle2 className="h-4 w-4" /> {primaryLabel}
        </button>
        <button className="btn-warning" onClick={() => setAction('RETURN')}>
          <RotateCcw className="h-4 w-4" /> Return for revision
        </button>
        <button className="btn-danger" onClick={() => setAction('REJECT')}>
          <XCircle className="h-4 w-4" /> Reject
        </button>
      </div>

      {c && (
        <Confirm
          open
          title={c.title}
          message={c.message}
          confirmLabel={c.label}
          tone={c.tone}
          requireReason={c.reason}
          reasonLabel={c.reasonLabel}
          onCancel={() => setAction(null)}
          onConfirm={(reason) => submit(action!, reason)}
        />
      )}

      {blocked && (
        <Modal open onClose={() => setBlocked(null)}
               title={`${blocked.posting_month} is closed`}
               subtitle={`${docType} ${docId} cannot post into it`}
               footer={<>
                 <button className="btn-secondary" onClick={() => setBlocked(null)} disabled={moving}>
                   Leave it — I will ask Accounts to reopen
                 </button>
                 <button className="btn-primary" disabled={moving}
                         onClick={async () => {
                           setMoving(true)
                           const { error } = await supabase.rpc('rpc_repost_to_open_period', {
                             p_doc_type: docType, p_doc_id: docId, p_remarks: null,
                           })
                           if (error) { setMoving(false); toast.error(error.message); return }
                           setBlocked(null)
                           setMoving(false)
                           toast.success(`Moved to ${blocked.suggested_month}. Approve it now.`)
                           onDone?.()
                         }}>
                   {moving ? 'Moving…' : `Post to ${blocked.suggested_month} instead`}
                 </button>
               </>}>
          <Alert tone="warning" title="Nothing has been approved, and no stock has moved">
            {blocked.reason}
          </Alert>

          <div className="mt-3 space-y-3 text-sm">
            <div>
              <p className="font-semibold text-slate-800">
                Either — ask Accounts or the administrator to reopen {blocked.posting_month}
              </p>
              <p className="text-slate-600">
                Right when the figures genuinely belong in that month and the books can still
                take them. Come back and approve once it is open. The CMD is told whenever a
                month is reopened.
              </p>
            </div>
            <div>
              <p className="font-semibold text-slate-800">
                Or — post it to {blocked.suggested_month} instead
              </p>
              <p className="text-slate-600">
                The document <strong>keeps its own date</strong>. Only the month the figures
                land in moves — exactly what would have happened had it been entered today.
                Accounts is notified, because a receipt they expected in{' '}
                {blocked.posting_month} turning up later is the surprise the close exists to
                prevent.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

/** What happens when the LAST signature in the chain is given. */
function chainApproveFinal(docType: string): string {
  return docType === 'PO'
    ? 'This is the final signature. The order becomes APPROVED, the PDF is generated on the company letterhead over the CMD\'s name, and it can be sent to the vendor.'
    : 'This is the final signature. The requisition becomes APPROVED and a Procurement Executive can turn it into a purchase order.'
}

function approveMessage(docType: string): string {
  switch (docType) {
    case 'PR':
      return 'The requisition moves to the Procurement Executive, who will collect quotations offline and raise the purchase order.'
    case 'GRN':
      return 'The accepted quantity is added to the site inventory immediately, and a vendor payable is raised for the accepted quantity only — rejected material is excluded.'
    case 'MIN':
      return 'The issued quantity is deducted from the site inventory immediately.'
    case 'MRN':
      return 'The returned quantity is added back to the site inventory immediately.'
    default:
      return 'The document moves to the next stage.'
  }
}
